import {
    LEAVE_AREA_REASONS,
    PROTOCOLS,
    AreaRequestData,
    MaybeSystemMessage,
    OptionalSystemMessage,
    ReceivedMessage,
    ISystem,
    System,
    PreConfigData,
  invokeOrReturn,
  resolveSystemEntry,
  GAME_FILE_SYSTEM_OWNER,
  HOT_CODE_REQUEST,
  HOT_CODE_SYSTEM,
  type HotCodeRequest,
  type HotCodeResult,
  bufferToBase64,
  base64ToArrayBuffer,
  clearObj,
} from "@gotti/core";
import type { AssetAction, AssetChange } from '@gotti/core';
import { BaseServer, BaseServerConfig, RegisteredServerData } from "../BaseServer.js";
import { ProtocolArray } from "../ServerConnector/BaseServerConnector.js";
import { ServerProcess, PeerAreaData, mergePreloads } from "../ServerProcess.js";
import type { ServerAssetsFacade } from "../assets/assetsFacade.js";
import type { IServerSystem as IServerSystemType, ClientBoundMessage } from "../IServerSystem.js";
import { ConnectorServerConfigOptions } from "./ConnectorServer.js";
import { MASTER_AREA_PROTOCOLS, SERVER_TYPES, LEAVE_CHANNEL_REASONS, type LeaveChannelReason } from "../constants.js";
import { MasterServerConfigOptions } from "./Master.js";
import { ServerGameFile, makeGameFileData } from "../ServerGameFile.js";
import type { PostConfigGameData, ServerAreaFile, ServerEntryDataResolver } from "../ServerAreaFile.js";
import { AREA_START_ONLY_KEYS, AREA_FILE_SYSTEM_OWNER, GAME_START_ONLY_KEYS, changedKeys } from "../ServerAreaFile.js";
import type { ConnectorChangePayload } from "../types.js";

/** Maps LEAVE_AREA_REASONS to the corresponding LEAVE_CHANNEL_REASONS */
const AREA_TO_CHANNEL_REASON: Record<LEAVE_AREA_REASONS, LEAVE_CHANNEL_REASONS> = {
    [LEAVE_AREA_REASONS.DISCONNECTED]: LEAVE_CHANNEL_REASONS.DISCONNECTED,
    [LEAVE_AREA_REASONS.REMOVED_BY_AREA]: LEAVE_CHANNEL_REASONS.REMOVED_BY_AREA,
    [LEAVE_AREA_REASONS.SWITCHED_AREA]: LEAVE_CHANNEL_REASONS.SWITCHED_AREA,
};
import { getLogger, type GottiLogger } from "@gotti/core";
export interface IServerSystem {
    new (...args: Array<any>): System & IServerSystemType
}

export type AreaServerConfigOptions = {
    gameConfigData?: any,
    areaConfigData?: any,
    gameFile?: ServerGameFile,
    gameId?: string | number,
    areaFile: ServerAreaFile,
    // Marks this AreaServer as a dynamic instance so it reports membership to the
    // master (drives ref-counted auto-empty teardown).
    isDynamic?: boolean,
    /**
     * Filesystem root for this area's per-process Unified Assets API
     * (`this.$assets`), threaded from `servers.assets.path` by
     * `Starter.getBaseAreaConfig`. `ServerProcess` roots its per-process
     * `FsAssetSource` here (see `AreaServer.assetsPath`), falling back to
     * `<cwd>/assets` when unset.
     */
    assetsPath?: string,
}
export type AreaManagerServerConfig = {
    id: string,
    areas: Array<BaseServerConfig<AreaServerConfigOptions>>
}
export type AreaServerConfig = Omit<BaseServerConfig<AreaServerConfigOptions>, 'type'>

/** What `AreaServer.applySystemChanges` / `reloadAreaFile` did. Names are system names. */
export type SystemChangeResult = {
    restarted: Array<string | number>,
    /** Started — by `add`, or a `restart` entry whose system was not running. */
    added: Array<string | number>,
    removed: Array<string | number>,
    /** Asked to remove, but another file (the game file) still runs it. */
    notRemoved: Array<string | number>,
    /** `AREA_START_ONLY_KEYS` that differ in the new area file — not applied; they take effect when the area next starts. */
    startOnlyChanged: string[],
};

/** Per-client answers to `AreaServer.reloadClientSystems`. */
export type ClientReloadOutcome = {
    reloadId: string,
    clients: Array<{ clientId: string, ok: boolean, result?: HotCodeResult, error?: string }>,
};

/** A system change failed part-way; `result` is what was applied before the failing step. */
export class SystemChangeError extends Error {
    constructor(message: string, readonly result: SystemChangeResult, readonly cause?: unknown) {
        super(message);
        this.name = 'SystemChangeError';
    }
}
const { MASTER, CONNECTOR, AREA } = SERVER_TYPES;

export class AreaServer extends BaseServer {
    readonly batchedMessagesByArea : {
        [area: string]: {[system: string]: {[messageType: string]: Array<any> }
    }} = {};

    readonly : ServerProcess;

    private master : RegisteredServerData<MasterServerConfigOptions>;

    readonly clientIdToConnectorIdLookup : {[publicId: string]: string | number} = {};
    readonly activeClientConnectors: Array<string> = [];
    readonly clientConnectorCounts : {[connectorId: string]: number } = {};

    /**
     * Clients whose connector died: state is held (NOT evicted) until they
     * rejoin through a new connector within `reconnectGraceMs`, or the grace
     * timer expires and the real eviction runs via `handleClientLeave`.
     * Public + readonly — game code may inspect it (e.g. to render a
     * "reconnecting" indicator); mutate it only through `detachClient`.
     */
    public readonly detachedClients: Map<string, { connectorId: string | number, detachedAt: number }> = new Map();
    private readonly detachTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

    readonly activeConnectors: Array<string> = [];

    readonly sameProcessAreaServers : {[key: string]: AreaServer  } = {};
    readonly managedAreaServers : {[key: string]: AreaServer } = {};

    readonly bufferedAreaMessages : Array<{ areaId: string, message: any }> = [];
    readonly bufferedGlobalAreaMessages : Array<{ areaId: string, message: any }> = [];
    readonly bufferedAreaRequests : Array<{ areaId: string, message: AreaRequestData, resolve: (value: any) => void, reject: (reason?: any) => void }> = [];
    readonly bufferedMasterMessages: Array<any> = [];
    readonly bufferedMasterRequests: Array<{ request: any, resolve: any, reject: any }> = [];

    readonly gameSystems : Array<System & IServerSystemType> = [];
    readonly areaSystems : Array<System & IServerSystemType> = [];

    public gameFile? : ServerGameFile;

    readonly gameId?: string | number;
    readonly areaId: string | number;
    readonly isDynamic: boolean = false;

    private lastUsedGlobalConnectorIndex = 0;

    public areaFile : ServerAreaFile;

    /** Set by ServerProcess: apply a master asset-change to this process's manager. */
    public onAssetChange?: (action: AssetAction, change: AssetChange) => void;

    public gameProcess : ServerProcess;
    // Set when a topology / global-data message from the master's SYNCHRONOUS
    // registration broadcast lands BEFORE onAfterInit created `gameProcess`. This
    // is reachable now that a co-located area is registered with the master
    // (addLocalServer) BEFORE initialize() so preload reads can relay to it. The
    // process dispatches are skipped while `gameProcess` is unset and replayed
    // once systems are live — see updateGlobalAreas/updateGameAreas + replayTopologyToProcess.
    private topologyDeferredPreInit = false;

    private readonly connectors: Array<RegisteredServerData<ConnectorServerConfigOptions>> = [];
    private readonly globalConnectors: Array<RegisteredServerData<ConnectorServerConfigOptions>> = [];
    public areaState : any = {};

    public globalAreas: {[areaId: string]: PeerAreaData} = {};
    public gameAreas: {[areaId: string]: PeerAreaData} = {};

    // public gameState: State<any> = new State();

   // public areaState : State<any> = new State();
   // public gameState: State<any> = new State();

    public areaServerData : any = {};
    public gameServerData : any = {};
    public gameClientData : any = {};
    public areaClientData : any = {};

    readonly areaConfigData : any;
    readonly gameConfigData? : any;

    /**
     * Filesystem root for this area's per-process Unified Assets API,
     * threaded in from `AreaServerConfigOptions.assetsPath` (ultimately
     * `servers.assets.path`). Read by `ServerProcess.onAddedSystem` when it
     * lazily builds the per-process `FsAssetSource`/`ServerAssetManager`.
     */
    readonly assetsPath?: string;

    public areaMetaData?: any;
    public gameMetaData?: any;

    private channelMap: {[name: string]: number} = {};
    private channelClients: {[name: string]: Set<string>} = {};
    private nextChannelIndex = 0;
    private channelRequestSeq = 0;
    private channelRequestTrackers: Record<string, {
      remaining: Set<string>;
      responses: Array<{ clientId: string, success: boolean, response: any, error?: string }>;
      resolve: (res: Array<{ clientId: string, success: boolean, response: any, error?: string }>) => void;
      timeoutId: NodeJS.Timeout;
      onResponse?: (res: { clientId: string, success: boolean, response: any, error?: string }) => void;
    }> = {};

    // private readonly removingPlayers : {[playerId: string]: boolean } = {};
    readonly logger : GottiLogger;

    constructor(c : AreaServerConfig) {
        super({ ...c, type: AREA });       
        this.gameFile = c.options.gameFile ?? c.options.gameFile;
        this.areaFile = c.options.areaFile;
        this.gameId = c.options.gameId ?? c.options.gameId;
        this.areaId = c.id;
        this.isDynamic = !!c.options.isDynamic;
        this.areaConfigData = c.options.areaConfigData;
        this.gameConfigData = c.options.gameConfigData ?? c.options.gameConfigData;
        this.assetsPath = c.options.assetsPath;

        this.logger = getLogger(`Game:${this.gameId}-Area:${this.areaFile.type}::${this.areaId}:`, {
            kind: 'area', serverId: this.id, areaId: this.areaId,
            ...(this.gameId !== undefined && this.gameId !== null ? { gameId: this.gameId } : {}),
        });

        this.handleConnectionMade = this.handleConnectionMade.bind(this);
        this.handleConnectionClosed = this.handleConnectionClosed.bind(this);
        this.onConnectionMade(this.handleConnectionMade);
        this.onConnectionClosed(this.handleConnectionClosed);
    }

    public override onMasterBroadcast(message: ReceivedMessage) {
        this.gameProcess.systemArray?.forEach(s => {
            s?.onMasterGlobalBroadcast?.(message);
        });
    }

    public getState() : any {
        // Server-authoritative area state (what systems read via `$areaState`).
        // Passed to `onJoinRequest` as `areaStateData` so joins can gate on state.
        return this.areaState?.data ?? {};
    }


    /*
    private async invokeDataGetters(file: { data?: any, id: string | number, type: string | number  }) : Promise<{ server: any, client: any }> {
        if(file?.data) {
            if(typeof file?.data === 'function') {
                return file.data({ id: this.id, type: file.type });
            } else {
                const client = await invokeOrReturn(file?.data?.client || {}, { id: file.id, type: file.type });
                const server = await invokeOrReturn(file?.data?.server || {}, { id: file.id, type: file.type });
                const shared = await invokeOrReturn(file?.data?.shared || {}, { id: file.id, type: file.type });
            }
        } else {
            return { server: {}, client: {} }
        }
    }*/


    public override async onAfterInit(): Promise<void> {
        // Create the game process up front — BEFORE the data factories — so its
        // one memoized asset manager can be populated by preloadAssets before any
        // data/api factory runs; those factories read preloaded assets
        // synchronously through the $assets facade. attachAreaServer wires the
        // process to this server (ensureAssetManager relays over requestMaster);
        // the full initialize() at the tail is idempotent and reuses this same
        // process + manager (built exactly once).
        this.gameProcess = new ServerProcess();
        this.gameProcess.attachAreaServer(this);
        // Preload the game + area asset paths. No-op — and no manager built —
        // when there are none, so asset-less games boot unchanged. Fail-loud: a
        // bad preload rejects boot before any factory runs.
        await this.gameProcess.preloadAssets(mergePreloads(this.gameFile?.preloads, this.areaFile?.preloads));
        // $assets facade for the DATA factories, built ONLY when a data factory
        // exists (an asset-less game never builds the manager here — preserving a
        // byte-identical boot). The api factories get their own facade inside
        // ServerProcess._initGameFileApis; both resolve to the same manager.
        const $assets = (this.gameFile?.data || this.areaFile?.data || this.areaFile?.state)
          ? this.gameProcess.getAssetsFacade()
          : undefined;

        if(this.gameFile?.data) {
           const { client, server } = await makeGameFileData(this.gameFile, this.gameId, this.gameConfigData, false, $assets);
           this.gameServerData = server;
           this.gameClientData = client;
        }

        // this.gameMetaData = await makeGameFileMetaData(this.gameFile, this.gameId, this.gameConfigData);

        if(this.areaFile?.data || this.areaFile?.state) {
            const areaConfig : PreConfigData = {
                id: this.areaId,
                type: this.areaFile.type,
                configData: this.areaConfigData,
            }

            const dataFactoryParams : { game?: any, area: any, $assets?: ServerAssetsFacade } = {
                area: areaConfig,
                $assets,
            }
            if(this.gameId !== undefined && this.gameId !== null) {
                const gameConfig : PostConfigGameData = {
                    id: this.gameId,
                    type: this.gameFile.type,
                    data: {
                        server: this.gameServerData,
                        client: this.gameClientData,
                    },
                };
                dataFactoryParams.game = gameConfig;
            }

            if(typeof this.areaFile?.data === 'function') {
                const { server, client } = await this.areaFile.data(dataFactoryParams);
                this.areaClientData = client;
                this.areaServerData = server;
            } else if(this.areaFile?.data) {
                const data : ServerEntryDataResolver = this.areaFile.data;
                const sharedAreaData = 'shared' in data
                  ?  await invokeOrReturn(data?.shared || {}, dataFactoryParams)
                  : {}
                const clientAreaData = 'client' in data
                  ?  await invokeOrReturn(data?.client || {}, dataFactoryParams)
                  : {}
                const serverAreaData = 'server' in data
                  ? await invokeOrReturn(data?.server || {}, dataFactoryParams)
                  : {}
                this.areaClientData = {
                    ...sharedAreaData,
                    ...clientAreaData,
                 };
                this.areaServerData = {
                    ...sharedAreaData,
                    ...serverAreaData
                };
            }

            // Per-INSTANCE state seam: seed the live areaState from the file's
            // declarative factory. `server`/`shared` → areaState.data (what
            // systems read via $areaState and mutate via setState/updateState);
            // `client`/`shared` → areaState.client (what clients receive on
            // join + state syncs). Runs with the SAME params as `data`, so
            // dynamic instances see their createArea configData here.
            if(this.areaFile?.state) {
                if(typeof this.areaFile.state === 'function') {
                    const { server, client } = (await this.areaFile.state(dataFactoryParams)) ?? {};
                    this.areaState.data = server ?? {};
                    this.areaState.client = client ?? {};
                } else {
                    const stateResolver : ServerEntryDataResolver = this.areaFile.state;
                    const sharedState = 'shared' in stateResolver
                      ? await invokeOrReturn(stateResolver?.shared || {}, dataFactoryParams)
                      : {}
                    const clientState = 'client' in stateResolver
                      ? await invokeOrReturn(stateResolver?.client || {}, dataFactoryParams)
                      : {}
                    const serverState = 'server' in stateResolver
                      ? await invokeOrReturn(stateResolver?.server || {}, dataFactoryParams)
                      : {}
                    this.areaState.data = {
                        ...sharedState,
                        ...serverState,
                    };
                    // Deep-copy the shared slice for the client view: areaState is
                    // MUTABLE (setState/updateState, systems writing $areaState), so
                    // a shallow spread would alias nested shared objects between the
                    // server-private view and what clients receive on join/state-sync
                    // — a server-side mutation would silently leak to clients.
                    this.areaState.client = {
                        ...structuredClone(sharedState),
                        ...clientState,
                    };
                }
            }
        }
        // gameProcess was created + attached at the top of onAfterInit (so its
        // asset manager could preload before the data factories ran).
        this.refreshUpdateHooks();
        this.gameProcess.setGameData(this.gameServerData);
        this.gameProcess.setAreaData(this.areaServerData);
        this.logger.debug(`Initializing game process for area: ${this.areaId}`)
        // Derive simulation options: area overrides game when both present
        const fixedTimeStep = (this.areaFile?.fixedTimeStep ?? this.gameFile?.fixedTimeStep) ?? false;
        const timeStepSec = (this.areaFile?.timeStep ?? this.gameFile?.timeStep);
        await this.gameProcess.initialize(this, this.gameFile, this.areaFile, {
          fixedTimeStep,
          timeStepSec,
        });
        this.logger.debug(`Finished initializing game process for area: ${this.areaId}`)
        // If the master's synchronous registration broadcast landed before
        // gameProcess existed, its process dispatches were skipped — replay the
        // current topology snapshot now that systems are live (reproduces the
        // pre-wiring-order timing where these fired post-systems at wire time).
        if(this.topologyDeferredPreInit) {
          this.replayTopologyToProcess();
        }
    }
    
    private refreshUpdateHooks() {
        const checkFuncs = ['BeforeUpdate', 'AfterUpdate'];
        checkFuncs.forEach(f => {
            if(this.gameFile?.[`on${f}`]) {
                if(this.areaFile?.[`on${f}`]) {
                    this.gameProcess[`on${f}`]((delta) => {
                        this.gameFile[`on${f}`](this, delta);
                        this.areaFile[`on${f}`](this, delta);
                    })
                } else {
                    this.gameProcess[`on${f}`]((delta) => {
                        this.gameFile[`on${f}`](this, delta);
                    })
                }
            
            } else if (this.areaFile?.[`on${f}`]) {
                this.gameProcess[`on${f}`]((delta) => {
                    this.areaFile[`on${f}`](this, delta);
                })
            } else {
                this.gameProcess[`off${f}`]();
            }
        })
    }
    
    public onUpdateGameFile(gameFile: ServerGameFile) {        
        gameFile.systems.forEach(s => {
           this.gameProcess.restartOrStartSystem(s as ISystem | System)
        });
        this.gameFile = gameFile;
        this.refreshUpdateHooks();
    }

    /** Restart every system `areaFile` lists and replace the area file's hooks. Prefer `reloadAreaFile`, which diffs. */
    public onUpdatedAreaFile(areaFile: ServerAreaFile) {
        this.applySystemChanges({ restart: areaFile.systems ?? [] }, areaFile);
    }

    /**
     * Swap in a new version of this area's FILE without touching its systems:
     * `onJoinRequest` is rebound, `onBeforeUpdate`/`onAfterUpdate` are re-wired,
     * and every other hook (`onClientJoined`, `onClientGameMessage`, …) is read
     * from the new file from now on. Keys in `AREA_START_ONLY_KEYS` (data/state/
     * api factories, preloads, loop timing, debug) are read only when an area
     * starts — those that differ are returned in `startOnlyChanged`, NOT applied.
     * The `type` must not change.
     */
    public replaceAreaFile(next: ServerAreaFile): { startOnlyChanged: string[] } {
        if (!next || typeof next !== 'object') throw new Error(`replaceAreaFile: expected an area file object.`);
        if (String(next.type) !== String(this.areaFile.type)) {
            throw new Error(`replaceAreaFile: area ${this.areaId} runs type '${String(this.areaFile.type)}'; the replacement has type '${String(next.type)}'.`);
        }
        if (!this.gameProcess) throw new Error(`replaceAreaFile: area ${this.areaId} has not started.`);
        const startOnlyChanged = changedKeys(this.areaFile, next, AREA_START_ONLY_KEYS);
        if (next.onJoinRequest) {
            this.gameProcess.onJoinRequest = next.onJoinRequest.bind(this.gameProcess);
        } else {
            delete this.gameProcess.onJoinRequest;
        }
        this.areaFile = next;
        this.refreshUpdateHooks();
        return { startOnlyChanged };
    }

    /**
     * Apply system changes to this area's running process, then (with
     * `nextAreaFile`) replace the area file's hooks via `replaceAreaFile`:
     *
     * - `remove`: system names to remove (the area file's ownership is dropped;
     *   a system the game file also runs stays — reported as not removed).
     * - `restart`: entries (class / instance / factory) hot-swapped through
     *   `restartOrStartSystem` — rolled back individually if one throws. An entry
     *   whose system is not running is started (reported in `added`).
     * - `add`: entries to start; an entry whose system already runs is an error
     *   (restart it instead).
     *
     * Order: remove → restart → add → area file. On a failure the method throws
     * a `SystemChangeError` whose `result` says what was applied before it.
     */
    public applySystemChanges(
        changes: { restart?: unknown[], add?: unknown[], remove?: Array<string | number> },
        nextAreaFile?: ServerAreaFile,
    ): SystemChangeResult {
        return this.applySystemChangesAs(AREA_FILE_SYSTEM_OWNER, changes, nextAreaFile ? () => this.replaceAreaFile(nextAreaFile).startOnlyChanged : undefined, 'replace area file');
    }

    private applySystemChangesAs(
        owner: string,
        changes: { restart?: unknown[], add?: unknown[], remove?: Array<string | number> },
        replaceFile?: () => string[],
        replaceStep = 'replace file',
    ): SystemChangeResult {
        const proc = this.gameProcess;
        if (!proc) throw new Error(`applySystemChanges: area ${this.areaId} has not started.`);
        const result: SystemChangeResult = { restarted: [], added: [], removed: [], notRemoved: [], startOnlyChanged: [] };
        let step = '';
        try {
            for (const name of changes.remove ?? []) {
                step = `remove '${String(name)}'`;
                if (!(name in proc.systemLookup)) throw new Error(`system '${String(name)}' is not running`);
                proc.removeSystem(name, owner);
                (name in proc.systemLookup ? result.notRemoved : result.removed).push(name);
            }
            for (const entry of changes.restart ?? []) {
                const { name, entry: resolved } = resolveSystemEntry(entry, proc);
                step = `restart '${String(name)}'`;
                const wasRunning = name in proc.systemLookup;
                proc.restartOrStartSystem(resolved as ISystem | System, owner);
                (wasRunning ? result.restarted : result.added).push(name);
            }
            for (const entry of changes.add ?? []) {
                const { name, entry: resolved } = resolveSystemEntry(entry, proc);
                step = `add '${String(name)}'`;
                if (name in proc.systemLookup) throw new Error(`system '${String(name)}' is already running — restart it instead`);
                proc.addSystemsArrayFromFile([resolved as ISystem | System], owner);
                result.added.push(name);
            }
            if (replaceFile) {
                step = replaceStep;
                result.startOnlyChanged = replaceFile();
            }
        } catch (err) {
            throw new SystemChangeError(`area ${String(this.areaId)}: ${step}: ${err instanceof Error ? err.message : String(err)}`, result, err);
        }
        return result;
    }

    /**
     * Send a hot-code request to clients in this area (all joined clients, or
     * `opts.clientIds`) and collect each answer. A client applies it only if it
     * runs `HotCodeSystem` (@gotti/client); one that does not — or that is
     * detached, or does not answer within `timeoutMs` (default 10s) — is
     * reported with `ok: false` and its error. Never throws for a client's failure.
     */
    public async reloadClientSystems(
        request: Omit<HotCodeRequest, 'reloadId'> & { reloadId?: string },
        opts: { clientIds?: string[], timeoutMs?: number } = {},
    ): Promise<ClientReloadOutcome> {
        const reloadId = request.reloadId ?? `hot-${Date.now().toString(36)}-${(this.hotReloadSeq++).toString(36)}`;
        const data: HotCodeRequest = { ...request, reloadId };
        const clientIds = opts.clientIds ?? Object.keys(this.gameProcess?.clients ?? {});
        const timeoutMs = opts.timeoutMs ?? 10_000;
        const clients = await Promise.all(clientIds.map(async (clientId) => {
            try {
                const result = await this.requestClient(clientId, { type: HOT_CODE_REQUEST, data, to: HOT_CODE_SYSTEM }, timeoutMs) as HotCodeResult;
                if (!result || result.reloadId !== reloadId) throw new Error(`client answered without a hot-code result for ${reloadId}`);
                return { clientId, ok: result.errors.length === 0, result, ...(result.errors.length ? { error: result.errors.join('; ') } : {}) };
            } catch (err) {
                return { clientId, ok: false, error: err instanceof Error ? err.message : String(err) };
            }
        }));
        return { reloadId, clients };
    }
    private hotReloadSeq = 0;

    /**
     * Hot-reload this area to a new version of its area file: systems the new
     * file no longer lists are removed, new ones are added, retained ones are
     * restarted — all of them, or only `opts.restart` (names) when the caller
     * knows which changed — and the area file's hooks are replaced. Which
     * systems belong to the area file is read from the running process
     * (`AREA_FILE_SYSTEM_OWNER`), never by re-constructing the old file's
     * entries.
     */
    public reloadAreaFile(next: ServerAreaFile, opts: { restart?: Array<string | number> } = {}): SystemChangeResult {
        const proc = this.gameProcess;
        if (!proc) throw new Error(`reloadAreaFile: area ${this.areaId} has not started.`);
        if (String(next?.type) !== String(this.areaFile.type)) {
            throw new Error(`reloadAreaFile: area ${this.areaId} runs type '${String(this.areaFile.type)}'; the new file has type '${String(next?.type)}'.`);
        }
        const running = new Set(Object.keys(proc.systemOwners).filter(n => proc.systemOwners[n]?.has(AREA_FILE_SYSTEM_OWNER)));
        const nextEntries = (next.systems ?? []).map(e => resolveSystemEntry(e, proc));
        const dup = nextEntries.find((e, i) => nextEntries.findIndex(o => String(o.name) === String(e.name)) !== i);
        if (dup) throw new Error(`reloadAreaFile: area file '${String(next.type)}' lists system '${String(dup.name)}' twice.`);
        const nextNames = new Set(nextEntries.map(e => String(e.name)));
        const only = opts.restart ? new Set(opts.restart.map(String)) : undefined;
        return this.applySystemChanges({
            remove: [...running].filter(n => !nextNames.has(n)),
            restart: nextEntries.filter(e => running.has(String(e.name)) && (!only || only.has(String(e.name)))).map(e => e.entry),
            add: nextEntries.filter(e => !running.has(String(e.name))).map(e => e.entry),
        }, next);
    }

    /**
     * Hot-reload this area's GAME file (the game's own systems + its
     * `onBeforeUpdate`/`onAfterUpdate` hooks): systems the old game file listed
     * and the new one does not are removed, new ones added, retained ones
     * restarted (all, or only `opts.restart`). `GAME_START_ONLY_KEYS` that differ
     * are reported, not applied. The master-side half (`getInitialAreas`) is the
     * Starter's `reloadGame`.
     */
    public reloadGameFile(next: ServerGameFile, opts: { restart?: Array<string | number> } = {}): SystemChangeResult {
        const proc = this.gameProcess;
        if (!proc) throw new Error(`reloadGameFile: area ${this.areaId} has not started.`);
        if (!this.gameFile) throw new Error(`reloadGameFile: area ${this.areaId} runs no game file.`);
        if (String(next?.type) !== String(this.gameFile.type)) {
            throw new Error(`reloadGameFile: area ${this.areaId} runs game type '${String(this.gameFile.type)}'; the new file has type '${String(next?.type)}'.`);
        }
        const isGameOwned = (n: string) => !!proc.systemOwners[n]?.has(GAME_FILE_SYSTEM_OWNER);
        // What the RUNNING game file listed (named from the process's constructor map — no construction for classes it ran).
        const previous = new Set((this.gameFile.systems ?? []).map(e => String(resolveSystemEntry(e, proc).name)).filter(n => n in proc.systemLookup && isGameOwned(n)));
        const nextEntries = (next.systems ?? []).map(e => resolveSystemEntry(e, proc));
        const nextNames = new Set(nextEntries.map(e => String(e.name)));
        const only = opts.restart ? new Set(opts.restart.map(String)) : undefined;
        return this.applySystemChangesAs(GAME_FILE_SYSTEM_OWNER, {
            remove: [...previous].filter(n => !nextNames.has(n)),
            restart: nextEntries.filter(e => previous.has(String(e.name)) && (!only || only.has(String(e.name)))).map(e => e.entry),
            add: nextEntries.filter(e => !previous.has(String(e.name)) && !(String(e.name) in proc.systemLookup)).map(e => e.entry),
        }, () => {
            const startOnlyChanged = changedKeys(this.gameFile!, next, GAME_START_ONLY_KEYS);
            this.gameFile = next;
            this.refreshUpdateHooks();
            return startOnlyChanged;
        }, 'replace game file');
    }

    private handleAddConnector(data: RegisteredServerData<ConnectorServerConfigOptions>) {
        this.connectors.push(data);
        if(!(data.options.areas)) {
            this.globalConnectors.push(data);
            this.bufferedAreaMessages.forEach(b => {
                this.dispatchToArea(b.areaId, b.message);
            });
            this.bufferedAreaMessages.length = 0;
          this.flushBufferedAreaRequests();
        }
    }

    private handleRemoveConnector(data: RegisteredServerData<ConnectorServerConfigOptions>) {
        const idx = this.connectors.findIndex(c => c.id === data.id);
        if(idx > -1) {
            this.connectors.splice(idx, 1);
        }
        const idx2 = this.globalConnectors.findIndex(c => c.id === data.id);
        if(idx2 > -1) {
            this.globalConnectors.splice(idx2, 1);
            if(this.lastUsedGlobalConnectorIndex >= this.globalConnectors.length) {
                this.lastUsedGlobalConnectorIndex = 0;
            }
        }
        // The connector's clients are unreachable now — detach them (hold
        // state, start the reconnect-grace timer) instead of evicting, so a
        // client that reconnects through a NEW connector rebinds without
        // losing game state or membership. Grace expiry runs the real
        // eviction via handleClientLeave.
        Object.keys(this.clientIdToConnectorIdLookup).forEach(clientId => {
            if(this.clientIdToConnectorIdLookup[clientId] === data.id) {
                this.detachClient(clientId);
            }
        });
    }

    /**
     * Master's cluster-wide fan-out of a connector topology change
     * (MASTER_AREA_PROTOCOLS.CONNECTOR_CHANGE). `removed` is idempotent with
     * the direct registration-close path: whichever of the two events (this
     * fan-out, or the connector's own connection to this area dying —
     * handleConnectionClosed -> handleRemoveConnector) arrives first runs the
     * real detach; the second is a no-op because handleRemoveConnector's own
     * first action removes the connector from `this.connectors`, so the
     * "still present" guard below only passes once per connector.
     * `added`/`draining` are no-ops here today — the actual connector link
     * this area uses still comes from the registration handshake (Task 8);
     * nothing user-visible fires for either.
     */
    private handleConnectorChange(payload: ConnectorChangePayload) {
        if (payload.op === 'removed') {
            if (this.connectors.find(c => c.id === payload.connectorId)) {
                this.handleRemoveConnector({ id: payload.connectorId } as RegisteredServerData<ConnectorServerConfigOptions>);
            }
        }
    }

    /**
     * How long (ms) this area holds a detached client's state for a possible
     * rejoin. Resolved from the AREA FILE first, then the GAME FILE as a
     * default, then off. The area file's policy REPLACES the game file's — an
     * area can turn reconnect back OFF. (Deliberately NOT the same rule as
     * `preventMultiClientAreas`, which is `gameFile || areaFile`: either-true
     * wins there, so an area cannot opt out of it.)
     * `0` means off: a disconnect evicts immediately (see `detachClient`).
     *
     * Public because the connector needs it on CLIENT_JOINED_AREA so the gate
     * can size its own auth hold to the longest-held area.
     */
    public get reconnectGraceMs(): number {
        const policy = this.areaFile?.reconnect ?? this.gameFile?.reconnect;
        if (!policy?.enabled) return 0;
        return policy.graceMs > 0 ? policy.graceMs : 0;
    }

    /**
     * Connector gone (crash or topology change): hold this client's state
     * and wait for a rejoin instead of evicting. No `onClientLeft`, no
     * `gameProcess.removeClient`, no `reportAreaMembership(false)` —
     * membership never lapses. Resolves via `handleClientJoin`'s rebind path
     * (a rejoin within the grace window — which is unconditional: a held seat
     * IS the admission decision, `onJoinRequest` is never consulted for it),
     * `evictDetachedClient` (grace window elapses with no rejoin), or
     * `clearAllDetachTimers` (the area itself tears down before either
     * happens).
     */
    public detachClient(publicId: string) {
        // Reconnect grace is OPT-IN (the AREA file's `reconnect` policy, or the
        // game file's as its default — see `reconnectGraceMs` above). With no positive
        // grace — the DEFAULT — a disconnect evicts IMMEDIATELY (original pre-failover
        // behavior) instead of holding the player's state for a reconnect window.
        // Both disconnect entry points (connector loss, client DISCONNECTED) route
        // through here, so this one guard makes eviction immediate when grace is off.
        if (this.reconnectGraceMs <= 0) {
            this.handleClientLeave(publicId, LEAVE_AREA_REASONS.DISCONNECTED);
            return;
        }
        // detachedClients / detachTimers are keyed by STRING publicId — mirroring
        // clientIdToConnectorIdLookup, a plain object that coerces its keys. The
        // bulk connector-death loop supplies strings (Object.keys), but a rejoin
        // arrives with the raw wire id, which is a NUMBER under UDP +
        // useSequentialPublicIds — so every Map access normalizes with String().
        const key = String(publicId);
        const connectorId = this.clientIdToConnectorIdLookup[publicId];
        if (connectorId == null || this.detachedClients.has(key)) return;
        this.detachedClients.set(key, { connectorId, detachedAt: Date.now() });
        delete this.clientIdToConnectorIdLookup[publicId];
        this.removeConnectorId(connectorId);            // broadcasts stop fanning to the dead connector
        const t = setTimeout(() => this.expireDetachedClient(key), this.reconnectGraceMs);
        t.unref?.();
        this.detachTimers.set(key, t);
        this.areaFile?.onClientDisconnected?.(this, publicId, {
            leaving: false,
            reason: LEAVE_AREA_REASONS.DISCONNECTED,
            reconnectTimeout: this.reconnectGraceMs,
        });
        // NOTE: no onClientLeft, no gameProcess.removeClient, no reportAreaMembership(false).
    }

    /**
     * Run the real eviction for a currently-detached client: the grace window
     * elapsed with no rejoin. Keeps the restore-then-leave symmetry in one
     * place (a rejoin can no longer be rejected, so this is the only way a
     * held seat ever ends without a rebind). Restores the
     * lookup so handleClientLeave's connector bookkeeping (delete +
     * removeConnectorId) no-ops cleanly, symmetric with the removeConnectorId
     * call detachClient made when it detached. No-op if the client isn't
     * currently detached (e.g. it already rebound or was already evicted).
     */
    private evictDetachedClient(publicId: string) {
        const entry = this.detachedClients.get(String(publicId));
        if (!entry) return;
        this.clientIdToConnectorIdLookup[publicId] = entry.connectorId;
        this.addConnectorId(entry.connectorId);
        this.handleClientLeave(publicId, LEAVE_AREA_REASONS.DISCONNECTED);
        // handleClientLeave's clearDetached() finds this entry and returns
        // true, so it skips a duplicate onClientDisconnected — exactly one
        // per connection episode, per the hook-ordering contract.
    }

    /** Grace window elapsed with no rejoin: run the real eviction. */
    private expireDetachedClient(publicId: string) {
        this.evictDetachedClient(publicId);
    }

    /**
     * A rejoin has BEGUN processing for a detached client (its
     * CLIENT_JOINED_AREA / REQUEST_JOIN_AREA landed). The grace timer's job —
     * "evict if no reconnect arrives in time" — is now done, so disarm it while
     * KEEPING the detachedClients entry: the rebind that follows reads that
     * entry to know this is a rejoin, and `handleClientJoin`'s `clearDetached`
     * is what finally drops it (timer included). Disarming here as well keeps
     * "a landed rejoin can never be evicted by its own grace timer" true at the
     * entry point, independent of how the rebind below is sequenced.
     */
    private cancelDetachExpiry(publicId: string | number): void {
        const key = String(publicId);
        const t = this.detachTimers.get(key);
        if (t) { clearTimeout(t); this.detachTimers.delete(key); }
    }

    /**
     * Resolve a join that arrived over the REAL reconnect transport
     * (GET_FIRST_AREAS → ConnectorServer → per-area CLIENT_JOINED_AREA),
     * distinct from the client-initiated joinArea (handleTryJoinArea).
     *
     * A rejoin THIS AREA holds a seat for rebinds WITHOUT consulting
     * `onJoinRequest` — identical to the handleTryJoinArea short-circuit, so
     * the two rejoin entry points can't drift (a rejoin skipping the hook on
     * one path but not the other is a parity bug). The hook still runs, and
     * still decides, for a FIRST join: not-detached keeps the pre-existing
     * fail-open fallback — a falsy/throwing `onJoinRequest` still joins with
     * the exact options it would have before.
     */
    private async handleReconnectTransportJoin(publicId: string, auth: any, connectorId: string | number, incomingOptions: any): Promise<void> {
        const isRejoin = this.detachedClients.has(String(publicId));   // keys are string-normalized (see detachClient)
        if (isRejoin) {
            // A landed rejoin is no longer "waiting to reconnect" — disarm its
            // grace timer so nothing can evict the client mid-rebind.
            this.cancelDetachExpiry(publicId);
            // THIS AREA is holding a seat for THIS client, and the gate only
            // routed it here because its server-verified auth identity matched
            // that seat. Re-asking the game to authorize its own held player is
            // what forced every game to reimplement this check — so don't.
            this.handleClientJoin(publicId, auth, connectorId, incomingOptions);
            return;
        }
        if (!this.gameProcess?.onJoinRequest) {
            this.handleClientJoin(publicId, auth, connectorId, incomingOptions);
            return;
        }
        let accepted: any;
        try {
            accepted = await this.gameProcess.onJoinRequest(this, { id: publicId, auth, options: incomingOptions }, this.getState());
        } catch (err) {
            this.logger.warn(`onJoinRequest threw during CLIENT_JOINED_AREA: ${err?.message || err}`);
            this.handleClientJoin(publicId, auth, connectorId, incomingOptions);   // pre-existing first-join fail-open
            return;
        }
        // First join with a falsy result → pre-existing fail-open (joins with
        // the falsy result exactly as before).
        this.handleClientJoin(publicId, auth, connectorId, accepted);
    }

    /** @returns true if the client WAS detached (its onClientDisconnected already fired at detach time) */
    private clearDetached(publicId: string | number): boolean {
        const key = String(publicId);
        const was = this.detachedClients.delete(key);
        const t = this.detachTimers.get(key);
        if (t) { clearTimeout(t); this.detachTimers.delete(key); }
        return was;
    }

    /**
     * Drops every pending grace timer and detached-client entry outright —
     * NO eviction callbacks (no onClientLeft, no gameProcess.removeClient, no
     * reportAreaMembership). Used by stop()/close() teardown so a torn-down
     * area's timers can't fire `expireDetachedClient` minutes later against
     * an area (and gameProcess) that's already gone — e.g.
     * Starter.tearDownLocalArea disconnects co-located connectors (which
     * arms detach timers via handleRemoveConnector) BEFORE calling
     * area.close(). Idempotent and safe to call with nothing detached, so
     * both stop() and close() can call it unconditionally (close() also
     * calls stop()).
     */
    private clearAllDetachTimers() {
        this.detachTimers.forEach(t => clearTimeout(t));
        this.detachTimers.clear();
        this.detachedClients.clear();
    }

    private handleConnectionClosed(d: RegisteredServerData<any>) {
        if(d.type === CONNECTOR) {
            this.handleRemoveConnector(d);
        } else if(d.type === MASTER) {
            delete this.master;
        }
    }

    private handleConnectionMade(d: RegisteredServerData<any>) {
        if(d.type === MASTER) {
            this.master = d;
            this.bufferedMasterMessages.forEach(m => {
                this.dispatchToMaster(m);
            })
            this.bufferedMasterRequests.forEach(r => {
                this.requestMaster(r.request)
                    .then(r.resolve)
                    .catch(r.reject);
            });
            this.bufferedGlobalAreaMessages.forEach(r => {
                this.dispatchToArea(r.areaId, r.message);
            });
            this.bufferedGlobalAreaMessages.length = 0;
            this.bufferedMasterMessages.length = 0;
            this.bufferedMasterRequests.length = 0;
        } else if (d.type === CONNECTOR) {
            this.handleAddConnector(d);
        }
    }

    public async requestArea(areaId: string, message: AreaRequestData) : Promise<any> {
        const localArea = this.sameProcessAreaServers[areaId];
  //      console.log({ localArea, areaId, message})
        if(localArea) {
            if('to' in message) {
                    // console.log('returning to', !!localArea.gameProcess?.systemLookup[message.to as unknown as string])
                return localArea.gameProcess?.systemLookup[message.to as unknown as string]?.onAreaRequest(this.areaId as string, message);
            } else {
                return localArea.areaFile?.onAreaGameRequest?.(localArea, areaId, message);
            }
        } else {
            if(!this.globalConnector) {
              return new Promise((resolve, reject) => {
                this.bufferedAreaRequests.push({ areaId, message, resolve, reject });
              });
            } else {
              return this.sendAreaRequestViaConnector(areaId, message);
            }
        }
    }

    private async sendAreaRequestViaConnector(areaId: string, message: AreaRequestData) {
      const connector = this.globalConnector;
      if(!connector) {
        throw new Error(`No global connector available to relay area request.`);
      }
      const id = connector.id;
      this.updateGlobalConnectorIndex();
      // OMIT the trailing `to` slot when absent (never send null/undefined over
      // the wire) — the receiving area routes `=== undefined` to onAreaGameRequest.
      const request: ProtocolArray = [PROTOCOLS.AREA_REQUEST_RELAY, areaId, message.type, message.data];
      if (message.to !== undefined) request.push(message.to);
      return this.requestServer(id, request);
    }
    
    private flushBufferedAreaRequests() {
      if(!this.globalConnectors.length || !this.bufferedAreaRequests.length) {
        return;
      }
      const pending = this.bufferedAreaRequests.splice(0);
      pending.forEach(({ areaId, message, resolve, reject }) => {
        this.sendAreaRequestViaConnector(areaId, message)
          .then(resolve)
          .catch(err => {
            reject?.(err);
          });
      });
    }

    private updateGlobalConnectorIndex() {
        this.lastUsedGlobalConnectorIndex++;
        if(this.lastUsedGlobalConnectorIndex >= this.globalConnectors.length) {
            this.lastUsedGlobalConnectorIndex = 0;
        }
    }

    get globalConnector() {
        return this.globalConnectors[this.lastUsedGlobalConnectorIndex];
    }

    public dispatchToArea(areaId: string, message: OptionalSystemMessage) {
      const localArea = this.sameProcessAreaServers[areaId];
      if(localArea) {
        // onAreaMessage/onAreaGameMessage receive the SENDER's area id, not the destination's.
        // Cross-process, the connector injects the sender (ConnectorServer AREA_RELAY -> serverOpts.id),
        // and onAreaRequest already passes this.areaId. This local path must match: `this` is the
        // sending AreaServer, so this.areaId is the sender. (Passing the destination `areaId` here
        // collapsed every same-process registration onto the destination — e.g. all match areas
        // registering with the matchmaker looked like they came from the matchmaker itself.)
        if('to' in message) {
          localArea.gameProcess?.systemLookup[message.to as unknown as string]?.onAreaMessage(this.areaId as string, message);
        } else {
          localArea.areaFile?.onAreaGameMessage?.(localArea, this.areaId as string, message);
        }
      } else {
        if(this.globalAreas[areaId]) {
            if(!this.master) {
              this.bufferedGlobalAreaMessages.push({ areaId, message })
            } else {
              // OMIT the trailing `to` slot when absent (never send null/undefined
              // over the wire) — the receiving area routes `=== undefined` to the
              // area file's onAreaGameMessage.
              const masterRelay: ProtocolArray = [MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE, areaId, message.type, message.data];
              if (message.to !== undefined) masterRelay.push(message.to);
              this.sendToServer(this.master.id, masterRelay)
            }
        } else {
          if(!this.globalConnector) {
            this.bufferedAreaMessages.push({ areaId, message })
          } else {
            const id = this.globalConnector.id;
            this.updateGlobalConnectorIndex();
            // OMIT the trailing `to` slot when absent (never send null/undefined
            // over the wire) — receivers detect the game-hook route via
            // `toSystem === undefined`.
            const relay: ProtocolArray = [PROTOCOLS.AREA_RELAY, areaId, message.type, message.data];
            if (message.to !== undefined) relay.push(message.to);
            this.sendToServer(id, relay)
          }
        }
      }
    }

    public dispatchToAreas(areaIds: string[], message: OptionalSystemMessage) {
      areaIds.forEach(areaId => {
        this.dispatchToArea(areaId, message);
      })
        /*
        const areas : string[] = [];
        areaIds.forEach(areaId => {
            const localArea = this.sameProcessAreaServers[areaId];
            if(localArea) {
                localArea.gameProcess?.systemLookup[message.to as unknown as string]?.onAreaMessage(areaId, message);
            } else {
                if(!this.globalConnector) {
                    this.bufferedAreaMessages.push({ areaId, message })
                } else {
                    areas.push(areaId);
                }
            }
        });
        if(areas.length) {
            this.sendToServer(this.globalConnector.id, [PROTOCOLS.AREA_RELAYS, ...areas, message.to, message.type, message.data])
        }*/
    }

    // Live dynamic-instance topology, kept in sync by the master's
    // SYNC_DYNAMIC_AREA_DATA broadcast — readable by systems/area code that wants
    // to discover instances (id → { id, type, configData, generation }).
    public instances: {[areaId: string]: PeerAreaData & { generation?: number }} = {};

    public updateInstances(data: {[areaId: string]: PeerAreaData & { generation?: number }}) {
      clearObj(this.instances);
      for(const key in data) {
        this.instances[key] = data[key];
      }
    }

    public updateGlobalAreas(data: {[areaId: string]: PeerAreaData }) {
      const prev = { ...this.globalAreas };
      clearObj(this.globalAreas);
      // Data assignment always runs; the process dispatches are skipped (and
      // replayed once systems are live) when this lands pre-init — see topologyDeferredPreInit.
      if(!this.gameProcess) this.topologyDeferredPreInit = true;
      for(const key in data) {
        this.globalAreas[key] = data[key];
        if(!(key in prev)) {
          this.gameProcess?.dispatchGlobalAreaAdded(key, data[key].type, data[key].configData);
        } else {
          delete prev[key];
        }
      }
      for(const key in prev) {
        this.gameProcess?.dispatchGlobalAreaRemoved(key, prev[key].type, prev[key].configData);
      }
    }

    public updateGameAreas(data: {[areaId: string]: PeerAreaData }) {
      const prev = this.gameAreas;
      this.gameAreas = data;
      const gameId = (this.config.options as any)?.gameId;
      // Data assignment always runs; the process dispatches are skipped (and
      // replayed once systems are live) when this lands pre-init — see topologyDeferredPreInit.
      if(!this.gameProcess) this.topologyDeferredPreInit = true;
      for(const key in data) {
        if(!(key in prev)) {
          this.gameProcess?.dispatchGameAreaAdded(key, data[key].type, data[key].configData);
          // Author topology hook: an area appeared cluster-wide (e.g. a dynamic
          // area was create-or-joined). Cheap ping; safe to omit on the area file.
          this.areaFile?.onAreaAdded?.(this, { areaId: key, type: String(data[key].type), gameId });
        } else {
          delete prev[key];
        }
      }
      for(const key in prev) {
        this.gameProcess?.dispatchGameAreaRemoved(key, prev[key].type, prev[key].configData);
        this.areaFile?.onAreaRemoved?.(this, { areaId: key, type: String(prev[key].type), gameId });
      }
    }

    // Re-fire the process-facing topology dispatches for the CURRENT stored
    // snapshot once systems are live. Used only when a pre-init master broadcast
    // skipped them (topologyDeferredPreInit). The area-file onAreaAdded hooks are
    // NOT replayed here — they already fired in updateGameAreas pre-init (areaFile
    // is set in the constructor), so replaying only the process dispatches keeps
    // each hook firing exactly once, reproducing the pre-wiring-order timing.
    private replayTopologyToProcess() {
      this.topologyDeferredPreInit = false;
      for(const key in this.globalAreas) {
        const a = this.globalAreas[key];
        this.gameProcess.dispatchGlobalAreaAdded(key, a.type, a.configData);
      }
      for(const key in this.gameAreas) {
        const a = this.gameAreas[key];
        this.gameProcess.dispatchGameAreaAdded(key, a.type, a.configData);
      }
    }

    public dispatchToMaster(message: { type: string | number, data: any }) {
      if(this.master) {
        this.sendToServer(this.master.id, [MASTER_AREA_PROTOCOLS.AREA_MESSAGE, message.type, message.data]);
      } else {
        this.bufferedMasterMessages.push(message);
      }
    }

    public async requestMaster(request: { type: string | number, data: any }, timeout: number = 60000) : Promise<any> {
      if(this.master) {
        const requestPromise = this.requestServer(this.master.id, [request.type, request.data]);
        return Promise.race([
          requestPromise,
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error(`Master request timed out after ${timeout}ms`)), timeout);
          })
        ]);
      } else {
        return new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            const idx = this.bufferedMasterRequests.findIndex(r => r.resolve === resolve);
            if(idx > -1) {
              this.bufferedMasterRequests.splice(idx, 1);
            }
            reject(new Error(`Master request timed out after ${timeout}ms (buffered)`));
          }, timeout);
          this.bufferedMasterRequests.push({ 
            request, 
            resolve: (v: any) => { clearTimeout(timeoutId); resolve(v); }, 
            reject: (e: any) => { clearTimeout(timeoutId); reject(e); } 
          });
        });
      }
    }

    /**
     * Create a dynamic area from this area (author system API). Returns the new
     * area's id; this area becomes its owner (only it or a master may destroy it).
     * This is an explicit CREATE — a duplicate `(type, key)` throws. Use
     * `joinArea(type, { key })` on the client for create-or-join.
     */
    public async createArea(type: string, opts: { key?: string, gameId?: string, configData?: any, managed?: boolean, maxClients?: number, emptyTtlMs?: number } = {}) : Promise<{ areaId: string }> {
      if(!this.master) throw new Error(`Cannot createArea: area ${this.areaId} is not connected to a master.`);
      return this.requestServer(this.master.id, [MASTER_AREA_PROTOCOLS.CREATE_AREA, type, opts]);
    }

    /** Destroy a dynamic area this area created (author system API). */
    public async destroyArea(areaId: string, opts: { drain?: boolean, whenEmpty?: boolean, timeoutMs?: number, reason?: string } = {}) : Promise<void> {
      if(!this.master) throw new Error(`Cannot destroyArea: area ${this.areaId} is not connected to a master.`);
      return this.requestServer(this.master.id, [MASTER_AREA_PROTOCOLS.DESTROY_AREA, areaId, opts]);
    }

    public async requestClient(clientId: string, message: { type: string | number, data: any, to?: string | number }, timeout: number = 60000) : Promise<any> {
      if(!this.clientIdToConnectorIdLookup[clientId]) {
        // A DETACHED client is still a member (gameProcess/channel state held)
        // but is transiently unroutable while it reconnects — distinguish it
        // from an unknown/departed client so the (expected, catchable) rejection
        // an author sees is accurate. Rejection is the intended contract either
        // way: requestClient can't reach an unreachable client; guard per-call.
        if (this.detachedClients.has(String(clientId))) {
          throw new Error(`Client: ${clientId} is detached (reconnecting) — not reachable until it rebinds.`);
        }
        throw new Error(`Client: ${clientId} not found in area server client map.`);
      }
      const connectorId = this.clientIdToConnectorIdLookup[clientId];
      const requestPromise = this.requestServer(connectorId, [PROTOCOLS.SERVER_TO_CLIENT_REQUEST, clientId, message.type, message.data, message.to]);
      
      return Promise.race([
        requestPromise,
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Client request timed out after ${timeout}ms`)), timeout);
        })
      ]);
    }

    public stop() {
      this.clearAllDetachTimers();
      if(this.gameProcess) {
        this.gameProcess.stopLoop();
      }
    }

    public override close() {
      this.clearAllDetachTimers();
      this.stop();
      return super.close();
    }

    public addSameProcessAreaServer(server: AreaServer) {
      this.sameProcessAreaServers[server.id] = server;
      server.sameProcessAreaServers[this.id] = this;
    }
    public removeSameProcessAreaServer(server: AreaServer) {
      delete this.sameProcessAreaServers[server.id]
      delete server.sameProcessAreaServers[this.id];
    }

    public dispatchToClient(
      clientId: string, 
      message: ClientBoundMessage,
      reliable = false
    ) {
      const connectorServerId = this.clientIdToConnectorIdLookup[clientId];
      if('to' in message) {
        this.relayToClient(connectorServerId, clientId, JSON.stringify(
          [
            PROTOCOLS.SYSTEM_MESSAGE,
            message.type,
            message.data,
            message.to,
          ]));
      } else {
        this.relayToClient(connectorServerId, clientId, JSON.stringify(
          [
            PROTOCOLS.GAME_MESSAGE,
            message.type,
            message.data,
          ]));
      }
    }

    public dispatchSystemStateUpdate(system: string | number, newState: any) {
      const payload = JSON.stringify([PROTOCOLS.SYSTEM_STATE_SYNC, system, newState]);
      this.activeClientConnectors.forEach(c => {
        this.relayByAreaId(c, payload);
      });
    }

    private emitClientStateSync() {
      if(this.activeClientConnectors.length) {
        // The connector relays this payload VERBATIM to clients
        // (ConnectorServer.onServerRelayByAreaMessage), so the areaId must travel
        // IN the payload — the client parses [AREA_STATE_SYNC, areaId, state].
        const payload = JSON.stringify([PROTOCOLS.AREA_STATE_SYNC, this.areaId, this.areaState.client]);
        this.activeClientConnectors.forEach(c => {
          this.relayByAreaId(c, payload);
        })
      }
    }
    private emitServerStateSync() {
        // const payload = JSON.stringify([PROTOCOLS.AREA_STATE_SYNC, this.areaState.data]);
        /*
        this.activeClientConnectors.forEach(c => {
            this.relayByAreaId(c, payload);
        })
        */
    }

    public setState<T>(state: Partial<T>) {
      // `.data` is the server-authoritative view systems read via `$areaState`;
      // `.client` is what clients receive (on join AND on this sync). setState
      // REPLACES the area state, so both halves become the set value — otherwise
      // the sync below broadcasts a stale `.client` and the set value never lands.
      this.areaState.data = state;
      this.areaState.client = state;
      this.emitClientStateSync();
      this.emitServerStateSync();
    }

    public updateState<T>(state: Partial<T>) {
      // Merge into both halves for consistency with setState; unlike setState this
      // does NOT broadcast — clients see the merge on the next setState or rejoin.
      Object.assign(this.areaState.data, state);
      Object.assign(this.areaState.client ?? (this.areaState.client = {}), state);
    }
    
    public dispatchClientAreaStateUpdate(newState: any) {
      const payload = JSON.stringify([PROTOCOLS.AREA_STATE_UPDATE, newState]);
      this.activeClientConnectors.forEach(c => {
        this.relayByAreaId(c, payload);
      });
    }

    public dispatchToClients(clientIds: string[], message: ClientBoundMessage) {
      const byC : {[key: string]: Array<string> } = {};
      const uniqueConnectors: Array<string | number> = [];
      clientIds.forEach(c => {
        const connectorServerId = this.clientIdToConnectorIdLookup[c] || null;
        if(!connectorServerId) return;
      
        if(!(connectorServerId in byC)) {
          uniqueConnectors.push(connectorServerId);
          byC[connectorServerId] = [];
        }
        byC[connectorServerId].push(c);
      });
 
      const payload = 'to' in message ?
        JSON.stringify([PROTOCOLS.SYSTEM_MESSAGE, message.type, message.data, message.to]) :
        JSON.stringify([PROTOCOLS.GAME_MESSAGE, message.type, message.data]);

      uniqueConnectors.forEach(c => {
        this.relayToClients(c, byC[c], payload)
      });
    }

    public dispatchToAllClients(message: ClientBoundMessage) {
        if('to' in message) {
          const payload = JSON.stringify([PROTOCOLS.SYSTEM_MESSAGE, message.type, message.data, message.to]);
          this.activeClientConnectors.forEach(c => {
            this.relayByAreaId(c, payload)
          });
        } else {
          const payload = JSON.stringify([PROTOCOLS.GAME_MESSAGE, message.type, message.data]);
          this.activeClientConnectors.forEach(c => {
            this.relayByAreaId(c, payload)
          });
        }
    }

    public registerChannels(channelNames: string[]) {
        channelNames.forEach(name => {
            if (!(name in this.channelMap)) {
                this.channelMap[name] = this.nextChannelIndex++;
                this.channelClients[name] = new Set();
                this.gameProcess.systemArray.forEach(s => {
                    s.onChannelCreated?.(name, []);
                });
            }
        });
    }

    public createChannel(channelName: string, clientIds?: string[]) {
        if (channelName in this.channelMap) return;

        if (clientIds) {
            for(let i = 0; i < clientIds.length; i++) {
                const clientId = clientIds[i];
                if(!(clientId in this.clientIdToConnectorIdLookup)) {
                    throw new Error(`Client: ${clientId} was not in the area when creating channel: ${channelName}`);
                }
            }
        }

        this.channelMap[channelName] = this.nextChannelIndex++;
        this.channelClients[channelName] = new Set();
        this.gameProcess.systemArray.forEach(s => {
            s.onChannelCreated?.(channelName);
        });
        if (clientIds) {
            clientIds.forEach(clientId => {
                this.addClientToChannel(clientId, channelName);
            });
        }
    }

    public destroyChannel(channelName: string) {
        const channelIndex = this.channelMap[channelName];
        if (channelIndex === undefined) return;

        this.activeClientConnectors.forEach(connectorId => {
            this.sendToServer(connectorId, [
                PROTOCOLS.AREA_TO_CONNECTOR_DESTROY_CHANNEL,
                channelIndex
            ]);
        });

        if (this.channelClients[channelName]) {
            for (const clientId of this.channelClients[channelName]) {
                this.gameProcess.systemArray.forEach(s => {
                    s.onClientLeftChannel?.(clientId, channelName, LEAVE_CHANNEL_REASONS.CHANNEL_DESTROYED);
                });
            }
        }

        this.gameProcess.systemArray.forEach(s => {
            s.onChannelDestroyed?.(channelName);
        });

        delete this.channelMap[channelName];
        delete this.channelClients[channelName];
    }

    public addClientToChannel(clientId: string, channelName: string) {
        const channelIndex = this.channelMap[channelName];
        if (channelIndex === undefined) {
            throw new Error(`Channel ${channelName} not registered.`);
        }

        if(!(clientId in this.clientIdToConnectorIdLookup)) {
            throw new Error(`Client: ${clientId} was not in the area when adding to channel: ${channelName}`);
        }

        if (!this.channelClients[channelName]) {
            this.channelClients[channelName] = new Set();
        }
        if (this.channelClients[channelName].has(clientId)) return;
        this.channelClients[channelName].add(clientId);

        this.gameProcess.systemArray.forEach(s => {
            s.onClientJoinedChannel?.(clientId, channelName);
        });

        const connectorId = this.clientIdToConnectorIdLookup[clientId];
        if (connectorId) {
            this.sendToServer(connectorId, [
                PROTOCOLS.AREA_TO_CONNECTOR_ADD_TO_CHANNEL,
                clientId,
                channelIndex
            ]);
        }
    }

    public removeClientFromChannel(clientId: string, channelName: string, reason: LeaveChannelReason = LEAVE_CHANNEL_REASONS.REMOVED) {
        const channelIndex = this.channelMap[channelName];
        if (channelIndex === undefined) {
            throw new Error(`Channel ${channelName} not registered.`);
        }

        if (this.channelClients[channelName]?.has(clientId)) {
            this.channelClients[channelName].delete(clientId);
            this.gameProcess.systemArray.forEach(s => {
                s.onClientLeftChannel?.(clientId, channelName, reason);
            });
        }

        // If there are in-flight channel requests expecting this client, fail them immediately.
        for (const correlationId in this.channelRequestTrackers) {
          const tracker = this.channelRequestTrackers[correlationId];
          if (!tracker?.remaining?.has(clientId)) continue;

          const res = {
            clientId,
            success: false,
            response: null,
            error: `Client left channel during request (${reason})`,
          };
          tracker.responses.push(res);
          tracker.onResponse?.(res);
          tracker.remaining.delete(clientId);

          if (!tracker.remaining.size) {
            clearTimeout(tracker.timeoutId);
            delete this.channelRequestTrackers[correlationId];
            tracker.resolve(tracker.responses);
          }
        }

        const connectorId = this.clientIdToConnectorIdLookup[clientId];
        if (connectorId) {
            this.sendToServer(connectorId, [
                PROTOCOLS.AREA_TO_CONNECTOR_REMOVE_FROM_CHANNEL,
                clientId,
                channelIndex
            ]);
        }
    }

    public dispatchToChannel(channelName: string, message: MaybeSystemMessage) {
        const channelIndex = this.channelMap[channelName];
        if (channelIndex === undefined) {
            throw new Error(`Channel ${channelName} not registered.`);
        }
        
        const payload = 'to' in message ?
            JSON.stringify([PROTOCOLS.SYSTEM_MESSAGE, message.type, message.data, message.to]) :
            JSON.stringify([PROTOCOLS.GAME_MESSAGE, message.type, message.data]);

        this.activeClientConnectors.forEach(connectorId => {
            // Relay-frame: payload rides as its own frame (no envelope re-escape).
            this.relayToChannel(connectorId, channelIndex, payload);
        });
    }

    public getClientsInChannel(channelName: string): Array<string> {
        if (!this.channelClients[channelName]) return [];
        return Array.from(this.channelClients[channelName]);
    }

    public async requestChannel(
      channelName: string,
      message: MaybeSystemMessage,
      onResponse?: (res: { clientId: string, success: boolean, response: any, error?: string }) => void,
    ): Promise<Array<{ clientId: string, success: boolean, response: any, error?: string }>> {
        const channelIndex = this.channelMap[channelName];
        if (channelIndex === undefined) return [];

        // Only members we can actually reach (have a live connector) are part of
        // this request round. A DETACHED (reconnecting) member keeps its channel
        // membership but has no connector, so it can't be messaged — counting it
        // in `remaining` would stall the whole request to its timeout and report
        // it as a false timeout. It rejoins the channel for future requests when
        // it rebinds. (Matches pre-failover behaviour: an unreachable client was
        // evicted from the channel immediately, so it was never counted.)
        const clients = this.getClientsInChannel(channelName)
          .filter(clientId => this.clientIdToConnectorIdLookup[clientId] != null);
        if (!clients.length) return [];

        const correlationId = `${this.id}-ch-${++this.channelRequestSeq}-${Date.now()}`;
        const remaining = new Set<string>(clients);
        const responses: Array<{ clientId: string, success: boolean, response: any, error?: string }> = [];

        const resolvePromise = new Promise<Array<{ clientId: string, success: boolean, response: any, error?: string }>>((resolve) => {
          const timeoutId = setTimeout(() => {
            // mark remaining as timed out
            for (const clientId of Array.from(remaining)) {
              const res = { clientId, success: false, response: null, error: `Channel request timed out after 60000ms` };
              responses.push(res);
              onResponse?.(res);
              remaining.delete(clientId);
            }
            delete this.channelRequestTrackers[correlationId];
            resolve(responses);
          }, 60000);

          this.channelRequestTrackers[correlationId] = {
            remaining,
            responses,
            resolve,
            timeoutId,
            onResponse,
          };
        });

        // dispatch to each connector with the correlationId
        const connectorIds = new Set<string | number>();
        clients.forEach(clientId => {
          const connectorId = this.clientIdToConnectorIdLookup[clientId];
          if (connectorId !== undefined && connectorId !== null) {
            connectorIds.add(connectorId);
          }
        });

        connectorIds.forEach(connectorId => {
          this.sendToServer(connectorId, [
            PROTOCOLS.AREA_TO_CONNECTOR_CHANNEL_REQUEST,
            channelIndex,
            message.type,
            message.data,
            message.to,
            correlationId,
          ]);
        });

        return resolvePromise;
    }

    public dispatchRaw(data: ArrayBuffer) {
      const encoded = bufferToBase64(data);
      this.activeClientConnectors.forEach(connectorId => {
        // Relay-frame: the base64 payload rides as its own frame (no re-escape).
        this.relayRawToArea(connectorId, encoded);
      });
    }
     
    public replaceClientArea(clientId: string, areaId: string | number, opts?: any) : boolean {
      this.addClientToArea(clientId, areaId, opts);
      this.removeClientFromArea(clientId, opts);
      return true;
    }

    public addClientToArea(clientId: string, areaId: string | number, opts?: any) : boolean {
      if(clientId in this.clientIdToConnectorIdLookup) {
        const connectorId = this.clientIdToConnectorIdLookup[clientId];
        this.sendToServer(connectorId, [
          PROTOCOLS.CLIENT_ADDED_TO_AREA_BY_AREA,
          clientId, 
          areaId, 
          opts
        ])
        if(this.gameFile?.preventMultiClientAreas || this.areaFile.preventMultiClientAreas) {
          this.removeClientFromArea(clientId, opts);
        }
        return true;
      }
      return false;
    }

    public removeClientFromArea(clientId: string, opts: any) : boolean {
      const connectorId = this.clientIdToConnectorIdLookup[clientId];
      if(connectorId) {
        this.sendToServer(connectorId, [
          PROTOCOLS.CLIENT_LEFT_AREA,
          clientId, 
          /*LEAVE_AREA_REASONS.REMOVED_BY_AREA,* dont need to send reason because if the message comes from the area we can assume it on the connector. */
          opts
        ])
        this.handleClientLeave(clientId, LEAVE_AREA_REASONS.REMOVED_BY_AREA, opts);
        return true;
      }
      return false;
    }
        
    public handleClientMessage(message: Array<any>) {
      const clientId = message[0];
      const type = message[1];
      const data = message[2];
      // console.log('handleClientMessage', { clientId, type, data });
      if(message.length > 3) {
          const to = message[3];
          this.logger.debug(`handlingg client SYSTEM message from client: ${clientId}. \n Message Type: ${type} \n to system: ${to}`);
          const system = this.gameProcess.systemLookup[to];
          if(system) {
              system.onClientMessage?.(clientId, { to, type, data });
          } else {
              this.logger.error(`Client message to system: ${to} did not exist in the area's gameProcess.systemLookup`)
              this.logger.error(`All keys in the game process system lookup: ${Object.keys(this.gameProcess.systemLookup)}`)
          }
      } else {
          this.logger.debug(`handling client GAME message from client: ${clientId}. \n Message Type: ${type}`);
          this.areaFile?.onClientGameMessage?.(this, clientId, { type, data });
      }
    }
    
    /* private getGameState() : any {
        return 
    } */

    /**
     * `playerId` is `string | number`: it is the RAW WIRE publicId, and
     * `useSequentialPublicIds` mints NUMBERS (authRecordEncoding.ts) — which is
     * why every Map/Set access below normalizes with `String()`. It was typed
     * `string` until the two `handleTryJoinArea` call sites were made honest;
     * the narrow type had been masking that mismatch, not preventing it (see
     * `AreaServer.detach.test.ts`, "a NUMERIC publicId ... still rebinds").
     */
    public handleClientJoin(playerId: string | number, auth: any, connectorId: string | number, options?: any) {
      //console.log('handleClientJoin', { playerId, auth, options, connectorId });
      if(this.areaFile?.serverOnly) {
        throw new Error(`Client: ${playerId} is trying to join a server only area. Options were: ${JSON.stringify({ playerId, auth, options, connectorId })}`);
      }
      this.logger.debug(`Client: ${playerId} joined area: ${this.areaId} ${
          JSON.stringify({
            playerId, auth, options, connectorId
          })
      }`);
      // A rejoin within the grace window rebinds an already-held client
      // instead of a fresh join — clear the detach bookkeeping up front so
      // the membership block below is skipped for it.
      const rejoining = this.detachedClients.has(String(playerId));   // keys are string-normalized (see detachClient)
      if (rejoining) this.clearDetached(playerId);
      this.clientIdToConnectorIdLookup[playerId] = connectorId
      this.sendToServer(connectorId, [
        PROTOCOLS.CLIENT_JOINED_AREA,
        playerId,
        options,
        this.areaClientData,
        this.areaState.client,
        // preventMultiClientAreas
        !!this.gameFile?.preventMultiClientAreas || !!this.areaFile.preventMultiClientAreas,
        // Index 6 is the error slot EXCLUSIVELY — a successful join always
        // leaves it undefined so a plain truthy read of message[6] on the
        // connector reliably means "this join failed", never "here's a grace
        // window". reconnectGraceMs goes one slot further out.
        undefined,
        // How long THIS area holds the seat if the client drops — the connector
        // relays it to the gate, which sizes its own auth hold to the longest
        // window across the client's areas. 0 = this area does not hold seats.
        this.reconnectGraceMs,
      ]);
      this.addConnectorId(connectorId);
      // A rebind keeps the AREA's channel membership (nothing ran
      // handleClientLeave's channel purge), but the CONNECTOR's channel routing
      // table went with the dead socket: it is keyed by privateId, and
      // handleRemovedClient strips the client from every areaChannels entry on
      // disconnect. After a reload the privateId is new, and after a connector
      // failover the surviving connector never had an entry at all — so unless
      // the membership is re-published here, the player is "in" the channel but
      // every dispatchToChannel/requestChannel silently skips them. Re-publish
      // BEFORE the connection hook, so an onClientConnected that dispatches to a
      // channel already reaches the rebound player.
      if (rejoining) this.republishChannelRouting(playerId, connectorId);
      this.areaFile?.onClientConnected?.(this, { id: playerId, auth, options, joining: !rejoining, connectorId });
      if (!rejoining) {
        //console.log('adding client to game process', { id: playerId, auth, options });
        this.gameProcess.addClient({ id: playerId, auth, options });
        this.areaFile?.onClientJoined?.(this, { id: playerId, auth, options })      // fires ONCE per membership — never on rejoin
        this.reportAreaMembership(playerId, true);                                  // membership never went false
      }
    }

    /**
     * Re-send this client's channel memberships to `connectorId` after a rebind.
     *
     * Exactly the frames `addClientToChannel` sends, replayed — the connector's
     * handler is idempotent (it appends only if absent), so a rebind onto the
     * SAME connector costs nothing and a rebind onto a NEW one is repaired.
     *
     * Membership is looked up through `storedChannelMember` for the same reason
     * `detachedClients` is string-normalized: a rejoin arrives with the raw wire
     * id, which is a NUMBER under UDP + useSequentialPublicIds, while game code
     * adds clients to channels using the string keys of
     * `clientIdToConnectorIdLookup` — so neither form alone finds every member.
     */
    /**
     * The form `id` is ACTUALLY stored under in a channel's member set, or
     * `undefined` if it isn't a member.
     *
     * A member set can hold either form and there is no single canonical one:
     * game code calls `addClientToChannel` with whatever id it is holding — the
     * raw wire publicId, a NUMBER under UDP + `useSequentialPublicIds` — while
     * the leave/rebind paths frequently arrive string-normalized
     * (`Object.keys(clientIdToConnectorIdLookup)`, `String(publicId)`). Probing
     * only one form misses, and the miss is SILENT: the client is either left in
     * the channel forever (leaves) or never re-routed to its new connector
     * (rebinds). Both forms are probed, and the stored one is returned so the
     * caller deletes the key that is really there.
     */
    private storedChannelMember(members: Set<any>, id: string | number): string | number | undefined {
        const key = String(id);
        if (members.has(key)) return key;
        // Only try the numeric form for ids that round-trip exactly, so '007'
        // or 'abc' never probe as 7 / NaN.
        const numeric = Number(key);
        if (key !== '' && Number.isFinite(numeric) && String(numeric) === key && members.has(numeric)) {
            return numeric;
        }
        return undefined;
    }

    private republishChannelRouting(publicId: string | number, connectorId: string | number) {
        for (const channelName in this.channelClients) {
            const members = this.channelClients[channelName];
            if (this.storedChannelMember(members, publicId) === undefined) continue;
            const channelIndex = this.channelMap[channelName];
            if (channelIndex === undefined) continue;
            this.sendToServer(connectorId, [
                PROTOCOLS.AREA_TO_CONNECTOR_ADD_TO_CHANNEL,
                publicId,
                channelIndex
            ]);
        }
    }

    /**
     * If this area is a dynamic instance, tell the master a client joined/left so
     * it can ref-count for auto-empty teardown. Co-located → direct call; remote →
     * a single lightweight message (join/leave is infrequent vs game traffic).
     *
     * `clientId` is `string | number` because it is the RAW WIRE publicId, and
     * `useSequentialPublicIds` mints NUMBERS (see authRecordEncoding.ts). It is
     * STRING-NORMALIZED here, at the one place membership crosses to the master.
     *
     * That normalization is load-bearing, not cosmetic. The master's membership
     * list is a `string[]` matched with `indexOf`, and every OTHER producer of
     * that list already yields strings: leaves come through
     * `Object.keys(clientIdToConnectorIdLookup)` / `String(publicId)`, and the
     * master-restart reconciliation report builds `members` from
     * `Object.keys(...)` too (Starter.ts, the `report()` executor). Only the
     * JOIN report was raw, so under `useSequentialPublicIds` a join landed as
     * `5` and its leave as `'5'`: `indexOf('5')` on `[5]` is -1, the member was
     * never removed, `memberCount` stuck above zero, `removeMember` returned
     * `{empty:false}` forever — so `destroyArea({whenEmpty})` never completed
     * and an ephemeral instance's empty-reap never armed. The area leaked.
     * See the note on `handleClientJoin`.
     */
    private reportAreaMembership(clientId: string | number, joined: boolean) {
        if (!this.isDynamic || !this.master) return;
        this.sendToServer(this.master.id, [MASTER_AREA_PROTOCOLS.AREA_MEMBERSHIP, String(clientId), joined]);
    }

    public addConnectorId(connectorId: string | number) {
        if(this.clientConnectorCounts[connectorId]) {
            this.clientConnectorCounts[connectorId]++;
        } else {
            this.clientConnectorCounts[connectorId] = 1;
            this.activeClientConnectors.length = 0;
            this.activeClientConnectors.push(...Object.keys(this.clientConnectorCounts))
        }
    }
    public removeConnectorId(connectorId: string | number) {
        if(connectorId in this.clientConnectorCounts) {
            this.clientConnectorCounts[connectorId]--;
            if(this.clientConnectorCounts[connectorId] <= 0) {
                delete this.clientConnectorCounts[connectorId];
                this.activeClientConnectors.length = 0;
                this.activeClientConnectors.push(...Object.keys(this.clientConnectorCounts))
            }
        }
    }
    
    public handleClientLeave(id: string, reason: LEAVE_AREA_REASONS, opts?: any) {
        // Connection hook precedes the membership hook, deduped against an
        // earlier detach: a detach already fired onClientDisconnected for
        // this episode, so don't fire it again here.
        const wasDetached = this.clearDetached(id);
        if (!wasDetached) this.areaFile?.onClientDisconnected?.(this, id, { leaving: true, reason });

        const channelReason = AREA_TO_CHANNEL_REASON[reason] ?? LEAVE_CHANNEL_REASONS.REMOVED;
        // Resolve the form each channel really stores this client under before
        // removing: a single-form lookup MISSES when the set holds the other
        // one, and the miss is silent — the departed client stays in the channel
        // so every later dispatchToChannel targets a ghost. See
        // `storedChannelMember` for why neither form is canonical.
        for (const channelName in this.channelClients) {
            const stored = this.storedChannelMember(this.channelClients[channelName], id);
            if (stored === undefined) continue;
            this.removeClientFromChannel(stored as string, channelName, channelReason);
        }

        const connectorId = this.clientIdToConnectorIdLookup[id];
        delete this.clientIdToConnectorIdLookup[id];
        this.gameProcess.removeClient(id, opts);
        this.removeConnectorId(connectorId)

        this.areaFile?.onClientLeft?.(this, id, reason, opts);
        this.reportAreaMembership(id, false);
    }

    public async handleTryJoinArea(publicId: string | number, auth: any, connectorId: string | number, options?: any) : Promise<any> {
        if(this.areaFile?.serverOnly) {
            this.logger.warn(`Client: ${publicId} is trying to join a server only area. Options were: ${JSON.stringify({ publicId, auth, options, connectorId })}`);
            return false;
        }

        // Auto-set for any code downstream so it can tell a fresh join from a
        // reconnect — visibility only; the area itself decides rejoin-vs-fresh
        // via handleClientJoin's own detachedClients check, so this flag never
        // has to be trusted for correctness.
        const rejoining = this.detachedClients.has(String(publicId));   // keys are string-normalized (see detachClient)
        if(rejoining) {
            options = { ...(options || {}), rejoin: true };
            // A landed rejoin: disarm the grace timer so nothing can evict the
            // client mid-rebind.
            this.cancelDetachExpiry(publicId);
            // THIS AREA is holding a seat for THIS client, and the gate only
            // routed it here because its server-verified auth identity matched
            // that seat. Re-asking the game to authorize its own held player is
            // what forced every game to reimplement this check — so don't.
            // onJoinRequest still runs for genuine first joins below.
            this.handleClientJoin(publicId, auth, connectorId, options);
            return options;
        }

        if(!this.gameProcess.onJoinRequest && this.areaFile.allowAllJoins) {
          //console.log('skip on joiin request, allow all joins.')
            this.handleClientJoin(publicId, auth, connectorId, options);
            return options || true;
        }
        //console.log('calling on join request!!!')
        const newOpts = await this.gameProcess.onJoinRequest(this, {
            id: publicId,
            auth,
            options,
        }, this.getState());
        //console.log('FINISHED ONJOINREEQ AND NEWOPTS WAS', newOpts)
        if(newOpts) {
          //  const latestOpts = safeMerge(options, newOpts);
            //console.log({ newOpts, latestOpts })
            this.handleClientJoin(publicId, auth, connectorId, newOpts);
            return newOpts;
        }
        // A rejected FIRST join was never a member, so it gets no side effects
        // here (a rejoin never reaches this line — it returned above without
        // ever consulting the hook).
        return false;
    }

    public override async onServerRequest(server: RegisteredServerData<any>, payload: any): Promise<any> {
        //console.log('AREA SERVER IS INSIDE ON SERVER REQUEST')
        if(server.type === CONNECTOR) {
            //console.log('WAS CONNECTOR, PAYLOAD: ', payload)
            switch(payload[0]) {
                case PROTOCOLS.CLIENT_TO_SERVER_REQUEST: {
                    const type = payload[1];
                    const data = payload[2];
                    const clientId = payload[3];
                    // No-`to` = the sender OMITTED the trailing slot (client +
                    // connector both truncate — never send null/undefined over the
                    // wire), so an absent slot reads back as `undefined`.
                    // NOT truthiness: system id 0 is valid.
                    const to = payload[4];
                    if(to !== undefined) {
                        const toSystem = this.gameProcess.systemLookup[to];
                        const handler = toSystem?.onClientRequest
                        if(!handler) throw new Error(`No handler for request ${type} to system ${to}`)
                        return handler.call(toSystem, clientId, { type, data, to });
                    } else {
                        const handler = this.areaFile?.onClientGameRequest;
                        if(!handler) throw new Error(`No handler for game request ${type}`)
                        return handler(this, clientId, { type, data, });
                    }
                }
                case PROTOCOLS.AREA_REQUEST_RELAY: {
                  //  console.log('WAS AREA REQUEST RELAY')
                    const areaId = payload[1];
                    const type = payload[2];
                    const data = payload[3];
                    const to = payload[4];
                    this.logger.debug(`Received area request relay. from area: ${server.id}, to area: ${areaId}, Type: ${type} Data: ${data} To system: ${to}`);

                    if(to !== undefined) {
                       // console.log('to was not undefined, do system requst', { areaId, type, data, to })
                        const toSystem =  this.gameProcess.systemLookup[to];
                        return toSystem?.onAreaRequest.call(toSystem, areaId, { type, data });
                    } else {
                      //  console.log('to was undefined... do on game request.')
                        return this.areaFile?.onAreaGameRequest?.(this, areaId, { type, data });
                    }

                    
                }
            }
        } else if (server.type === MASTER) {
            const protocol : MASTER_AREA_PROTOCOLS = payload[0];
            switch(protocol) {
                case MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE: {
                    // protocol, fromAreaId, type, data, toSystem, 
                    if(payload[4] !== undefined) {
                        const system = this.gameProcess.systemLookup[payload[4]];
                        return system?.onAreaRequest.call(system, payload[1], { type: payload[2], data: payload[3] })
                    } else {
                        return this.areaFile?.onAreaGameRequest?.(this, payload[1], { type: payload[2], data: payload[3] });
                    }
                }
                    
                case MASTER_AREA_PROTOCOLS.AREA_REQUEST: {
                    // protocol, type, data, toSystem — sent by master.requestArea.
                    // No-`to` = the master OMITTED the trailing slot (never send
                    // null/undefined over the wire); absent reads as `undefined`.
                    if(payload[3] !== undefined) {
                        const system = this.gameProcess.systemLookup[payload[3]];
                        return system?.onGlobalRequest?.call(system, { type: payload[1], data: payload[2] })
                    } else {
                        return this.areaFile?.onGlobalGameRequest?.(this, { type: payload[1], data: payload[2] });
                    }
                }
            }
        }
    }

    handleClientRequestResponse(clientId: string, seq: number, toSystem: string | number, responseData: any) {
        throw new Error(`Unimplemented`)
    }
    
    override onServerMessage(server: RegisteredServerData<any>, parsed: any) {
        // console.log('calling area server onServerMessage...', { server, parsed })
        if(server.type === CONNECTOR) {
            const protocol = parsed[0];
            switch(protocol) {
                // this one isnt handled as a traditional request with a response because 
                // we want the directional flow for joining areas always to go from area > client
                case PROTOCOLS.REQUEST_JOIN_AREA:
                    this.handleTryJoinArea(parsed[1], parsed[2], server.id, parsed[3]);
                    break;
                case PROTOCOLS.CLIENT_JOINED_AREA:
                    // A reconnect lands HERE (the rejoin transport:
                    // GET_FIRST_AREAS → handleGetFirstAreas → this send), NOT on
                    // the REQUEST_JOIN_AREA/handleTryJoinArea path a
                    // client-initiated joinArea uses.
                    void this.handleReconnectTransportJoin(parsed[1], parsed[2], server.id, parsed[3]);
                    break;
                case PROTOCOLS.AREA_RELAY: {
                    const fromArea = parsed[1];
                    const type = parsed[2];
                    const data = parsed[3];
                    const toSystem = parsed[4];
                    // No-`to` = the sender OMITTED the trailing slot (never send
                    // null/undefined over the wire — bandwidth), so an absent slot
                    // reads back as `undefined`. NOT truthiness: system id 0 is valid.
                    if(toSystem === undefined) {
                        this.areaFile?.onAreaGameMessage?.(this, fromArea, { data, type });
                    } else {
                        this.gameProcess.systemLookup[toSystem]?.onAreaMessage(fromArea, { data, type });
                    }
                    break;
                }
                case PROTOCOLS.CLIENT_TO_SERVER_MESSAGE: {
                    //console.log('CLIENT TO SERVER MESSAGE:')
                    this.handleClientMessage(parsed.slice(1));
                    break;
                }
                case PROTOCOLS.CLIENT_TO_SERVER_RAW_MESSAGE: {
                    const clientId = parsed[1];
                    const encoded = parsed[2];
                    const buffer = base64ToArrayBuffer(encoded);
                  const systems = this.gameProcess.rawClientMessageSystems;
                  for (let i = 0; i < systems.length; i++) {
                    systems[i].onRawClientMessage!(clientId, buffer);
                  }
                    break;
                }
                case PROTOCOLS.GAME_MESSAGE: {
                  this.areaFile?.onClientGameMessage?.(this, parsed[1], {
                    type: parsed[2],
                    data: parsed[3]
                  });
                  break;
                }
                case PROTOCOLS.CONNECTOR_TO_AREA_CHANNEL_RESPONSE: {
                  const correlationId = parsed[1];
                  const clientId = parsed[2];
                  const success = parsed[3];
                  const response = parsed[4];
                  const error = parsed[5];

                  const tracker = this.channelRequestTrackers[correlationId];
                  if (!tracker) break;

                  // Ignore late/duplicate responses for clients we are no longer waiting on.
                  if (!tracker.remaining.has(clientId)) break;

                  const res = {
                    clientId,
                    success: !!success,
                    response: success ? response : null,
                    error: success ? undefined : (error ?? 'Unknown error'),
                  };

                  tracker.responses.push(res);
                  tracker.onResponse?.(res);
                  tracker.remaining.delete(clientId);

                  if (!tracker.remaining.size) {
                    clearTimeout(tracker.timeoutId);
                    delete this.channelRequestTrackers[correlationId];
                    tracker.resolve(tracker.responses);
                  }
                  break;
                }
                case PROTOCOLS.CLIENT_LEFT_AREA: {
                  const [, clientId, reason, opts, deliberate] = parsed;
                  if(reason === LEAVE_AREA_REASONS.DISCONNECTED && !deliberate) {
                    // Abnormal drop (no CLIENT_LEAVING preceded the socket
                    // close): hold state + grace timer instead of an absolute
                    // leave (a rejoin through this or another connector rebinds
                    // it — see handleClientJoin).
                    this.detachClient(clientId);
                  } else {
                    // Deliberate leave (client.close()/clearGame() → connector
                    // set `deliberate`) or an explicit non-DISCONNECTED reason:
                    // evict immediately. The DISCONNECTED reason is preserved so
                    // channel-leave reasons + onClientLeft(DISCONNECTED) match
                    // the pre-failover close() contract.
                    this.handleClientLeave(clientId, reason, opts);
                  }
                }
            }
        } else if (server.type === MASTER) {
            const protocol : MASTER_AREA_PROTOCOLS = parsed[0];
            switch(protocol) {
                // MASTER_AREA_PROTOCOLS.PREPARE_FOR_CONNECTOR is deliberately NOT
                // handled: it made this area connect to (and trust) any config
                // the message named, and nothing ever sent it. A connector reaches
                // an area through the registration handshake the Starter / master
                // choreograph. The ordinal stays reserved.
                case MASTER_AREA_PROTOCOLS.AREA_MESSAGE:
                    // No-`to` = the master OMITTED the trailing slot (never send
                    // null/undefined over the wire); absent reads as `undefined`.
                    if(parsed[3] !== undefined) {
                        this.gameProcess.systemLookup[parsed[3]]?.onMasterSystemMessage?.({ type: parsed[1], data: parsed[2] });
                    } else {
                        this.areaFile?.onGlobalMessage?.({ type: parsed[1], data: parsed[2] });
                    }
                    break;
                case MASTER_AREA_PROTOCOLS.RELAY_AREA_REQUEST_OR_MESSAGE:
                    // protocol, fromAreaId, type, data, toSystem — same no-`to` rule
                    // as AREA_RELAY: route to the area FILE's game hook.
                    if(parsed[4] === undefined) {
                        this.areaFile?.onAreaGameMessage?.(this, parsed[1], { type: parsed[2], data: parsed[3] });
                    } else {
                        this.gameProcess.systemLookup[parsed[4]]?.onAreaMessage(parsed[1], { type: parsed[2], data: parsed[3] });
                    }
                    break;
                case MASTER_AREA_PROTOCOLS.SYNC_GLOBAL_AREA_DATA:
                    this.updateGlobalAreas(parsed[1]);
                    break;
                case MASTER_AREA_PROTOCOLS.SYNC_DYNAMIC_AREA_DATA:
                    this.updateInstances(parsed[1]);
                    break;
                case MASTER_AREA_PROTOCOLS.SYNC_GAME_AREA_DATA:
                    this.updateGameAreas(parsed[1]);
                    break;
                case MASTER_AREA_PROTOCOLS.ASSET_CHANGED:
                    this.onAssetChange?.(parsed[1], parsed[2]);
                    break;
                case MASTER_AREA_PROTOCOLS.SET_GLOBAL_GAME_DATA:
                    this.gameServerData = parsed[1];
                    this.gameClientData = parsed[2];
                    // Pre-init (gameProcess not yet created) the data is stored above and
                    // applied by onAfterInit's own setGameData before systems start.
                    this.gameProcess?.setGameData(this.gameServerData);
                    break;
                case MASTER_AREA_PROTOCOLS.SET_GLOBAL_AREA_DATA:
                    this.areaServerData = parsed[1];
                    this.areaClientData = parsed[2];
                    this.gameProcess?.setAreaData(this.areaServerData);
                    break;
                case MASTER_AREA_PROTOCOLS.CONNECTOR_CHANGE:
                    this.handleConnectorChange(parsed[1]);
                    break;
            }
        }
    }
}